/**
 * Interaction-state helpers — disabled / focus / hover / press.
 *
 * Sourced from `tokens/semantic/state.json`. Components import these
 * helpers and spread the returned CSS into `styled()` so a token change
 * lands everywhere with no per-component edit. Mirrors how `text(role)`
 * works for typography.
 *
 * NOTE: values are mirrored here as plain numbers (not read from CSS
 * vars) because they're consumed inside emotion `styled()` callbacks
 * where the theme is already-resolved JS. Keep in sync with state.json.
 */

import type {CSSObject, Theme} from '@mui/material/styles';

// ── Source values ─────────────────────────────────────────────────────

export const STATE = {
  opacity: {
    disabled: 0.55,
    overlayHover: 0.08,
    overlayPress: 0.16,
  },
  focus: {
    ringWidth: 2,
    ringOffset: 2,
  },
} as const;

// ── disabledStyle ─────────────────────────────────────────────────────

/**
 * Apply uniform disabled fade + cursor change.
 *
 *     const Root = styled('button')(({ theme }) => ({
 *       ...someBase,
 *       '&:disabled': disabledStyle(),
 *     }));
 *
 * The fade is opacity-based (not a per-tone "disabled color") so any
 * fill — primary / bad / neutral / status — softens consistently.
 */
export function disabledStyle(): CSSObject {
  return {
    opacity: STATE.opacity.disabled,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  };
}

// ── focusRing ─────────────────────────────────────────────────────────

export interface FocusRingOptions {
  /**
   * Whether to apply via `&:focus-visible &` (good for parent-as-label,
   * where the visual ring sits on a nested element). Default `false`:
   * applies to the element itself via `&:focus-visible`.
   */
  parentFocusVisible?: boolean;
}

/**
 * Tone-agnostic focus ring. Outer ring inherits `currentColor`, inner
 * gap paints in `background.default` so the ring reads as a clean halo
 * on any surface tier.
 *
 *     const Root = styled('div')(({ theme }) => ({
 *       ...focusRing(theme),
 *     }));
 *
 * For controls where the focusable input is a sibling of the visual
 * element (Radio's hidden `<input>` next to its label-rendered box),
 * call `focusRing(theme, { parentFocusVisible: true })` and apply on
 * the VISUAL element — the selector becomes `label:has(input:focus-visible) &`.
 */
export function focusRing(_theme: Theme, options: FocusRingOptions = {}): CSSObject {
  const {ringWidth, ringOffset} = STATE.focus;
  // CSS-var, а не `theme.palette.background.default`: MUI v6 не конвертит
  // кастомные слоты палитры в var() при чтении в styled-каллбэке — литерал
  // активной схемы запекается в правило, и внутри SurfaceContextProvider
  // (Modal, светлая поверхность) внутренний зазор кольца остаётся тёмным.
  // `_theme` сохранён в сигнатуре ради совместимости всех call-site'ов.
  const bg = 'var(--ads-palette-background-default)';
  const ringStyle = {
    outline: 'none',
    // Two-shadow halo: inner = surface-color gap, outer = currentColor ring.
    boxShadow: `0 0 0 ${ringOffset}px ${bg}, 0 0 0 ${ringOffset + ringWidth}px currentColor`,
  };

  if (options.parentFocusVisible) {
    return {'label:has(input:focus-visible) &': ringStyle};
  }
  return {'&:focus-visible': ringStyle};
}

// ── stateOverlay ──────────────────────────────────────────────────────

export interface StateOverlayOptions {
  /** Diameter (in px) of the hover overlay. */
  hoverSize: number;
  /** Diameter (in px) of the press overlay. Larger than hover for "tap" feel. */
  pressSize: number;
  /**
   * Whether to enable hover/press on the ACTIVE state. For Radio you'd
   * pass `false` (an already-selected radio offers no action so no
   * affordance shown). For Checkbox `true` (selected checkbox can be
   * unchecked — affordance still relevant). Default `true`.
   */
  whenActive?: boolean;
}

/**
 * Circular hover-then-press overlay around an icon-sized control.
 *
 * Paints a `::before` pseudo centered on the parent — hovers grow it
 * to `hoverSize`, presses grow further to `pressSize`. Tint = currentColor
 * so it auto-matches the control's foreground (no extra color prop).
 *
 *     const Box = styled('span')(({ theme }) => ({
 *       position: 'relative',           // required for ::before centering
 *       ...stateOverlay({ hoverSize: 20, pressSize: 40 }),
 *     }));
 *
 * The parent MUST have `position: relative` for the absolute pseudo to
 * land in the right spot.
 *
 * Active-state suppression — pass `whenActive: false` to wrap the rules
 * in a `[data-active="false"]` selector so only inactive controls show
 * the overlay. The caller sets `data-active` on the styled element.
 */
export function stateOverlay({
  hoverSize,
  pressSize,
  whenActive = true,
}: StateOverlayOptions): CSSObject {
  const base = {
    content: '""',
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    width: hoverSize,
    height: hoverSize,
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    opacity: 0,
    pointerEvents: 'none' as const,
    transition: 'opacity 0.12s, width 0.12s, height 0.12s',
  };
  const hover = {
    opacity: STATE.opacity.overlayHover,
  };
  const press = {
    width: pressSize,
    height: pressSize,
    opacity: STATE.opacity.overlayPress,
  };

  if (whenActive) {
    return {
      '&::before': base,
      '&:hover::before': hover,
      '&:active::before': press,
    };
  }
  // Suppress overlay when [data-active="true"] on the styled element —
  // active Radio offers no interaction so we hide the affordance.
  return {
    '&::before': base,
    '&[data-active="false"]:hover::before': hover,
    '&[data-active="false"]:active::before': press,
  };
}
